/**
 * sz-activity-like — 活动点赞 API（通用）
 *
 * 1. POST /app-api/activity/like/visitor-token — 签发匿名访问者 Token
 * 2. POST /app-api/activity/like/count — 查询活动点赞数与条目数据
 * 3. POST /app-api/activity/like — 点赞活动组件（返回 likeCount、canLike）
 *
 * like / count 请求需 Header: X-Activity-Visitor-Token
 * 若返回 code === 1060000021，需重新调用 visitor-token 接口。
 */

(function (global) {
  'use strict';

  var LIKE_PATH = '/app-api/activity/like';
  var LIKE_COUNT_PATH = '/app-api/activity/like/count';
  var VISITOR_TOKEN_PATH = '/app-api/activity/like/visitor-token';
  var VISITOR_TOKEN_HEADER = 'X-Activity-Visitor-Token';
  var VISITOR_TOKEN_EXPIRED_CODE = 1060000021;

  /**
   * @typedef {Object} ActivityLikeReq
   * @property {string} activeCode
   * @property {string} componentCode
   */

  /**
   * @typedef {Object} ActivityLikeCountReq
   * @property {string} activeCode
   */

  /**
   * @typedef {Object} ActivityLikeEntry
   * @property {string} componentCode
   * @property {number} likeCount
   * @property {string} [cardImageUrl]
   * @property {string} [displayText]
   * @property {string} [displayDescription]
   * @property {string} [externalUrl]
   * @property {boolean} liked
   * @property {boolean} canLike
   */

  /**
   * @typedef {Object} ActivityLikeCountData
   * @property {ActivityLikeEntry[]} entries
   * @property {boolean} canLike
   */

  /**
   * @typedef {Object} ActivityLikeResult
   * @property {number|null} likeCount
   * @property {boolean} canLike
   */

  /**
   * @typedef {Object} ActivityLikeHeaders
   * @property {number|string} [tenantId]
   * @property {string} [authorization]
   * @property {string} [visitorToken]
   */

  function normalizeBase(base) {
    if (!base || typeof base !== 'string') return '';
    return base.replace(/\/+$/, '');
  }

  function buildApiUrl(baseUrl, path) {
    var base = normalizeBase(baseUrl);
    var combined = base + path;
    if (/^https?:\/\//i.test(combined)) return combined;
    var origin =
      typeof global.location !== 'undefined' && global.location.origin ? global.location.origin : '';
    if (!origin) throw new Error('SZ_ACTIVITY_LIKE_API: use absolute https API URL');
    var rel = combined.startsWith('/') ? combined : '/' + combined;
    return new URL(rel, origin).toString();
  }

  function buildRequestHeaders(headers, contentType) {
    var reqHeaders = {};
    if (contentType) reqHeaders['Content-Type'] = contentType;
    if (headers && headers.tenantId != null && headers.tenantId !== '') {
      reqHeaders['tenant-id'] = String(headers.tenantId);
    }
    if (headers && headers.authorization) {
      reqHeaders['Authorization'] = headers.authorization;
    }
    if (headers && headers.visitorToken) {
      reqHeaders[VISITOR_TOKEN_HEADER] = String(headers.visitorToken);
    }
    return reqHeaders;
  }

  function parseJsonResponse(res) {
    return res.json().then(function (json) {
      return { httpOk: res.ok, status: res.status, body: json };
    });
  }

  function postJson(baseUrl, path, body, headers) {
    return fetch(buildApiUrl(baseUrl, path), {
      method: 'POST',
      headers: buildRequestHeaders(headers, 'application/json'),
      body: JSON.stringify(body)
    }).then(parseJsonResponse);
  }

  /**
   * @param {string} baseUrl
   * @param {ActivityLikeHeaders} [headers]
   */
  function fetchVisitorToken(baseUrl, headers) {
    return fetch(buildApiUrl(baseUrl, VISITOR_TOKEN_PATH), {
      method: 'POST',
      headers: buildRequestHeaders(headers, 'application/x-www-form-urlencoded'),
      body: ''
    }).then(parseJsonResponse);
  }

  function unwrapEnvelope(result) {
    return result && result.body ? result.body : result;
  }

  function getResponseCode(result) {
    var envelope = unwrapEnvelope(result);
    return envelope && envelope.code != null ? envelope.code : null;
  }

  /**
   * @param {string} baseUrl
   * @param {ActivityLikeReq} body
   * @param {ActivityLikeHeaders} [headers]
   */
  function likeActivityComponent(baseUrl, body, headers) {
    return postJson(
      baseUrl,
      LIKE_PATH,
      {
        activeCode: body && body.activeCode != null ? String(body.activeCode) : '',
        componentCode: body && body.componentCode != null ? String(body.componentCode) : ''
      },
      headers
    );
  }

  /**
   * @param {string} baseUrl
   * @param {ActivityLikeCountReq} body
   * @param {ActivityLikeHeaders} [headers]
   */
  function fetchLikeCounts(baseUrl, body, headers) {
    return postJson(
      baseUrl,
      LIKE_COUNT_PATH,
      {
        activeCode: body && body.activeCode != null ? String(body.activeCode) : ''
      },
      headers
    );
  }

  function isLikeSuccess(result) {
    var envelope = unwrapEnvelope(result);
    return !!(envelope && envelope.code === 0 && envelope.data);
  }

  function isLikeCountSuccess(result) {
    var envelope = unwrapEnvelope(result);
    return !!(envelope && envelope.code === 0 && envelope.data && Array.isArray(envelope.data.likes));
  }

  function isVisitorTokenSuccess(result) {
    var envelope = unwrapEnvelope(result);
    return !!(envelope && envelope.code === 0 && envelope.data && envelope.data.visitorToken);
  }

  function isVisitorTokenExpired(result) {
    return getResponseCode(result) === VISITOR_TOKEN_EXPIRED_CODE;
  }

  function getVisitorToken(result) {
    var envelope = unwrapEnvelope(result);
    return envelope && envelope.data && envelope.data.visitorToken
      ? String(envelope.data.visitorToken)
      : '';
  }

  function readBoolField(obj, keys, fallback) {
    if (!obj || typeof obj !== 'object') return fallback;
    for (var i = 0; i < keys.length; i++) {
      var key = keys[i];
      if (obj[key] != null) return !!obj[key];
    }
    return fallback;
  }

  /**
   * @param {unknown} item
   * @returns {ActivityLikeEntry|null}
   */
  function normalizeLikeEntry(item) {
    if (!item || !item.componentCode) return null;
    var liked = readBoolField(item, ['liked', 'hasLiked', 'isLiked', 'loved'], false);
    var canLike = readBoolField(item, ['canLike', 'canLikeThis', 'allowLike'], null);
    if (canLike == null) canLike = !liked;
    return {
      componentCode: String(item.componentCode).trim(),
      likeCount: Number(item.likeCount) || 0,
      cardImageUrl: item.cardImageUrl != null ? String(item.cardImageUrl).trim() : '',
      displayText: item.displayText != null ? String(item.displayText) : '',
      displayDescription: item.displayDescription != null ? String(item.displayDescription) : '',
      externalUrl: item.externalUrl != null ? String(item.externalUrl).trim() : '',
      liked: liked,
      canLike: canLike
    };
  }

  /**
   * @param {Array<ActivityLikeEntry>} likes
   * @returns {Record<string, number>}
   */
  function likesToMap(likes) {
    var map = {};
    if (!Array.isArray(likes)) return map;
    likes.forEach(function (item) {
      var entry = normalizeLikeEntry(item);
      if (entry) map[entry.componentCode] = entry.likeCount;
    });
    return map;
  }

  /**
   * @param {Array<ActivityLikeEntry>} likes
   * @returns {ActivityLikeEntry[]}
   */
  function normalizeLikeEntries(likes) {
    if (!Array.isArray(likes)) return [];
    return likes.map(normalizeLikeEntry).filter(Boolean);
  }

  /**
   * @param {unknown} data
   * @returns {ActivityLikeCountData}
   */
  function normalizeLikeCountData(data) {
    if (!data || typeof data !== 'object') {
      return { entries: [], canLike: true };
    }
    return {
      entries: normalizeLikeEntries(data.likes),
      canLike: readBoolField(data, ['canLike', 'canContinueLike', 'canLikeMore', 'allowLike'], true)
    };
  }

  /**
   * @param {unknown} data
   * @returns {ActivityLikeResult|null}
   */
  function normalizeLikeResult(data) {
    if (!data || typeof data !== 'object') return null;

    var canLike = true;
    if (data.canLike != null) canLike = !!data.canLike;
    else if (data.canContinueLike != null) canLike = !!data.canContinueLike;
    else if (data.canLikeMore != null) canLike = !!data.canLikeMore;
    else if (data.allowLike != null) canLike = !!data.allowLike;

    return {
      likeCount: data.likeCount != null ? Number(data.likeCount) || 0 : null,
      canLike: canLike
    };
  }

  /**
   * @param {*} result
   * @returns {ActivityLikeResult|null}
   */
  function getLikeResultFromResult(result) {
    var envelope = unwrapEnvelope(result);
    return normalizeLikeResult(envelope && envelope.data);
  }

  global.SZ_ACTIVITY_LIKE_API = {
    fetchVisitorToken: fetchVisitorToken,
    likeActivityComponent: likeActivityComponent,
    fetchLikeCounts: fetchLikeCounts,
    isLikeSuccess: isLikeSuccess,
    isLikeCountSuccess: isLikeCountSuccess,
    isVisitorTokenSuccess: isVisitorTokenSuccess,
    isVisitorTokenExpired: isVisitorTokenExpired,
    getVisitorToken: getVisitorToken,
    normalizeLikeEntry: normalizeLikeEntry,
    normalizeLikeEntries: normalizeLikeEntries,
    normalizeLikeCountData: normalizeLikeCountData,
    normalizeLikeResult: normalizeLikeResult,
    getLikeResultFromResult: getLikeResultFromResult,
    likesToMap: likesToMap,
    VISITOR_TOKEN_EXPIRED_CODE: VISITOR_TOKEN_EXPIRED_CODE
  };
})(typeof window !== 'undefined' ? window : globalThis);
